import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useBannerListQuery, getNativeWebview } from '../react-queries/queries';
import { BannerType } from '../common/enums';
import { launchUrl } from '../api/native/NativeApis';

const BannerWrapper = styled.div`
  width: 100%;
  margin: 0 auto 16px auto;
  position: relative;
  /* overflow: hidden; */
`;
const BannerSlider = styled.div`
  width: 100%;
  height: ${({ $height }) => $height}px;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;
const BannerImg = styled.img`
  width: 100%;
  height: ${({ $height }) => $height}px;
  object-fit: cover;
  border-radius: 20px;
  display: block;
  position: absolute;
  left: 0;
  top: 0;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transform: translateX(${({ offset }) => offset}%);
  transition: opacity 0.5s, transform 0.5s;
  z-index: ${({ active }) => (active ? 2 : 1)};
  pointer-events: ${({ active }) => (active ? 'auto' : 'none')};
`;
const IndexBadge = styled.div`
  position: absolute;
  right: 18px;
  bottom: 12px;
  background: rgba(0,0,0,0.5);
  color: rgba(255,255,255,0.8);
  font-size: 13px;
  border-radius: 16px;
  padding: 2px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 10;
  pointer-events: none;
`;

const BANNER_DURATION = 5000;
const COUNT_TO_REFETCH = 5;
const FALLBACK_IMAGE = '/banner_sample.png';

const DEFAULT_BANNER_HEIGHT = 200;

const BookBanner = ({ bannerType = BannerType.MAIN, height = DEFAULT_BANNER_HEIGHT }) => {
  const navigate = useNavigate();
  const [fetchAt, setFetchAt] = useState(new Date());
  
 // const banners = data ?? [];
  const [banners, setBanners] = useState([]);
  //const total = banners.length;
  const [total, setTotal] = useState(banners.length);
  const [idx, setIdx] = useState(0);
  const IdxRef = useRef(0);
  const cntRef = useRef(0);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const didSwipeRef = useRef(false);
  const {data, refetch} = useBannerListQuery(bannerType);

  function fetchBanner() {
    console.log('fetchBanner', idx, total);
    if (IdxRef.current >= total - 1) {
        setIdx((prevIdx)=>{return 0});
        setFetchAt((prevFetchAt)=>{return new Date()});
        console.log('reset idx');
        if(cntRef.current++ >= COUNT_TO_REFETCH){
          cntRef.current = 0;
          refetch();
        }
        //refetch();
    } else {
        setIdx((prevIdx)=>{return prevIdx + 1});
    }
  }
  // 목록을 새로 받아오면 처음 배너부터 다시 시작
  useEffect(() => {
    setIdx(0);
    setBanners(data ?? []);
    console.log('data length', data?.length);
    setTotal(data?.length ?? 0);
  }, [data,fetchAt]);

  useEffect(() => {
    IdxRef.current = idx;
  }, [idx]);
  // 30초마다 다음 배너로, 마지막 배너까지 보여주면 목록을 다시 조회
  useEffect(() => {
    const timer = setInterval(fetchBanner, BANNER_DURATION);
    return () => clearInterval(timer);
  }, [total]);

  const onBannerClick = useCallback((link) => {
    if (!link) return;

    // 앱 내부 상대 경로 (예: /moim-detail?bookCommunityId=1)
    if (link.startsWith('/') && !link.startsWith('//')) {
      navigate(link);
      return;
    }

    try {
      const url = new URL(link, window.location.origin);
      // 같은 오리진이면 SPA 라우팅
      if (url.origin === window.location.origin) {
        navigate(`${url.pathname}${url.search}${url.hash}`);
        return;
      }
      // 외부 URL: 웹뷰는 네이티브 launchUrl, 브라우저는 새 창/이동
      if (getNativeWebview()) {
        launchUrl(url.href);
      } else {
        window.open(url.href, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      console.error('배너 링크 이동 실패:', e);
      if (getNativeWebview()) {
        launchUrl(link);
      } else {
        window.location.href = link;
      }
    }
  }, [navigate]);

  // 터치 스와이프
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
    didSwipeRef.current = false;
  };
  const onTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const onTouchEnd = () => {
    if (total > 1 && touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      if (diff > 40) {
        didSwipeRef.current = true;
        setIdx(idx === total - 1 ? 0 : idx + 1);
      } else if (diff < -40) {
        didSwipeRef.current = true;
        setIdx(idx === 0 ? total - 1 : idx - 1);
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleBannerClick = (link) => {
    // 스와이프 직후 click가 이어서 들어오면 무시
    if (didSwipeRef.current) {
      didSwipeRef.current = false;
      return;
    }
    onBannerClick(link);
  };

  if (total === 0) {
    return (
      <BannerWrapper>
        <BannerSlider $height={height}>
          <BannerImg src={FALLBACK_IMAGE} alt="배너" active offset={0} draggable={false} $height={height} />
        </BannerSlider>
      </BannerWrapper>
    );
  }

  return (
    <BannerWrapper
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <BannerSlider $height={height}>
        {banners.map((banner, i) => (
          <BannerImg
            key={`banner-${banner.id ?? i}`}
            src={banner.imgUrl || FALLBACK_IMAGE}
            alt={banner.title || `배너${i + 1}`}
            active={i === idx}
            offset={i === idx ? 0 : (i < idx ? -100 : 100)}
            draggable={false}
            onClick={() => handleBannerClick(banner.link)}
            style={{ cursor: banner.link ? 'pointer' : 'default' }}
            $height={height}
          />
        ))}
      </BannerSlider>
      <IndexBadge>
        {idx + 1} / {total}
      </IndexBadge>
    </BannerWrapper>
  );
};

export default BookBanner;
